import { Component, inject, Input, OnInit } from '@angular/core';
import { DefaultWarningsComponent } from '../default-warnings/default-warnings.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WarningGroup } from '../../models/error-warning.model';
import { ColDef } from '@cms_root/app/shared/grid/models/col-def.model';
import { ResourceStatusTranslation } from '@cms_root/app/modules/resources/models/resource-status';
import { getUserName } from '@cms_root/app/modules/resources/utils/resource.util';
import { formatDate, NgStyle } from '@angular/common';
import { GridModule } from '@cms_root/app/shared/grid/grid.module';
import { ResourceWarningGroup, ResourceWarningItem } from '@cms_root/app/modules/resources/models/resource-warnings';
import { RouterModule } from '@angular/router';
import { ResourceWarningObjectType } from '@cms_root/app/modules/resources/models/api/resource-dry-run-api.model';
import { ErrorWarningModalSource } from '../../models/error-warning-modal-data.model';
import { getColWidth } from '@cms_root/app/shared/grid/utils/grid-util';

@Component({
  selector: 'app-resource-warnings',
  templateUrl: './resource-warnings.component.html',
  styleUrls: ['./resource-warnings.component.scss'],
  imports: [NgStyle, RouterModule, TranslateModule, DefaultWarningsComponent, GridModule]
})
export class ResourceWarningsComponent implements OnInit {
  private readonly translateService = inject(TranslateService);

  @Input() warnings: WarningGroup[];
  @Input() source: ErrorWarningModalSource;

  resourceWarnings: ResourceWarningGroup;
  collectionWarnings: ResourceWarningGroup;
  defaultWarnings: WarningGroup[];

  rowStyles = { 'border-bottom': '1px solid #E7E7E7', padding: '20px 0' };

  colDefs: ColDef[] = [
    {
      title: 'resourcewarningspagename',
      width: 20,
      mapping: (item: ResourceWarningItem) => item.details?.objectName
    },
    {
      title: 'resourcewarningsstatus',
      width: 15,
      mapping: (item: ResourceWarningItem) =>
        item.details?.objectStatus
          ? this.translateService.instant(ResourceStatusTranslation[item.details?.objectStatus])
          : undefined
    },
    {
      title: 'resourcewarningsmodifiedby',
      width: 20,
      mapping: (item: ResourceWarningItem) => getUserName(item.details?.objectUpdatedBy)
    },
    {
      title: 'resourcewarningslastupdated',
      width: 20,
      mapping: (item: ResourceWarningItem) => this.formatItemDate(item.details?.objectUpdatedAt)
    },
    {
      title: 'resourcewarningsblocktypeelement',
      width: 20,
      mapping: (item: ResourceWarningItem) => item.details?.fieldName
    },
    {
      width: 5,
      templateIndex: 0
    }
  ];

  collectionColDefs: ColDef[] = [
    {
      title: 'resourcewarningscollectiontitle',
      width: 35,
      mapping: (item: ResourceWarningItem) => item.details?.objectName
    },
    {
      title: 'resourcewarningscreatedby',
      width: 30,
      mapping: (item: ResourceWarningItem) => getUserName(item.details?.objectUpdatedBy)
    },
    {
      title: 'resourcewarningslastupdated',
      width: 30,
      mapping: (item: ResourceWarningItem) => this.formatItemDate(item.details?.objectUpdatedAt)
    },
    {
      width: 5,
      templateIndex: 0
    }
  ];

  ngOnInit(): void {
    // it can be only one resource warning group
    this.resourceWarnings = this.warnings?.filter((item) => item.type === ResourceWarningObjectType.resource)?.[0];
    this.collectionWarnings = this.warnings?.filter(
      (item) => item.type === ResourceWarningObjectType.userCollection
    )?.[0] as ResourceWarningGroup;
    this.defaultWarnings = this.warnings?.filter(
      (item) =>
        item.type !== ResourceWarningObjectType.resource && item.type !== ResourceWarningObjectType.userCollection
    );
  }

  getColWidth(col: ColDef): any {
    return getColWidth(col);
  }

  private formatItemDate(date: string | number, format = 'mediumDate'): string {
    if (!date) {
      return undefined;
    }

    return formatDate(date, format, 'en_US');
  }
}
